/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/prop-types */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';

import scrollTo from 'src/client/js/view/utils/scrollTo';
import { UserUiConfigContext } from '../../hooks';
import { sortCards } from './helpers';

import Card from 'wonderbly-components/lib/Card';
import IconList from 'wonderbly-components/lib/IconList';
import ProductForm from '../ProductForm';
import RichText from '../Prismic/RichText';
import SectionHeading from '../SectionHeading';
import Grid from './Grid';
import ProductGridCarousel from './ProductGridCarousel';
import ProductGridMarketingCard from './ProductGridMarketingCard';

import './ProductGrid.scss';

const imageSizeOptions = ['contain', 'cover'];
const sizeOptions = ['extra-small', 'small', 'medium', 'large'];
let largeCardCount = 0;

const getProductCard = (
  (
    {
      ageRangeLabel,
      product,
      badgeOverride,
      colourOverride,
      imageSize,
      largeCardImageSide,
      largeCardPoint1,
      largeCardPoint1Icon,
      largeCardPoint2,
      largeCardPoint2Icon,
      priceLabel,
      savings,
      size,
      url,
      useCarousel,
      forceOpenForm = false,
      extraBottomSpacing = false,
      ctaBehaviour,
      showProductTags,
      productTags,
    },
    props,
    largeCardCount,
  ) => {
    const {
      productInformation: {
        backgroundColor,
        description,
        longDescriptionMd,
        title,
        image,
        productBadgeContent,
        productBadgeColour,
        carouselImages
      },
      main: { productId }
    } = product;

    let largeImageSide, cta;

    if (largeCardImageSide === 'auto') {
      // Alternate left and right in same grid
      largeImageSide = largeCardCount % 2 === 1 ? 'right' : 'left';
    } else {
      // Use user pref if explicitly set
      largeImageSide = largeCardImageSide;
    }

    const shouldShowCreatorForm =
      ctaBehaviour === 'show-creator-form';
    const shouldForceFormOpen =
      forceOpenForm && shouldShowCreatorForm;

    const isExpanded =
      props.expandedId === productId || shouldForceFormOpen;

    const renderImage = () => (
      <ProductGridCarousel
        backgroundColor={backgroundColor}
        images={carouselImages}
        imageSize={imageSize}
        size={size}
        id={productId}
      />
    );

    const renderAppendix = () => (
      <div id={`product-grid__card-appendix-${productId}`}>
        {isExpanded && (
          <ProductForm primary={{ product }} id={productId} />
        )}
      </div>
    );

    if (!shouldForceFormOpen) {
      cta = isExpanded
        ? props.ctaCloseLabel || 'Close the form'
        : props.ctaLabel || 'Create yours now!';
    }

    return (
      <Card
        ageRange={ageRangeLabel}
        badgeText={badgeOverride || productBadgeContent}
        badgeInline={size === 'medium'}
        body={
          size === 'large' && longDescriptionMd ? (
            <RichText fields={longDescriptionMd} />
          ) : (
            description
          )
        }
        colour={colourOverride || productBadgeColour || 'green'}
        cta={cta}
        image={image.url}
        imageBackgroundColour={backgroundColor}
        imageBackgroundSize={imageSize}
        large={size === 'large'}
        largeImageSide={largeImageSide}
        fadeButton={isExpanded}
        onClick={
          shouldShowCreatorForm
            ? () => {
              props.setExpandedId(isExpanded ? null : productId);

              if (!isExpanded && props.media.fabletDown) {
                scrollTo(
                  `#product-grid__card-appendix-${productId}`,
                  {
                    offset: 24,
                    collapsibleSelector:
                      props.expandedId &&
                      `#product-grid__card-appendix-${props.expandedId}`
                  }
                );
              }
            }
            : undefined
        }
        points={[
          largeCardPoint1 && (
            <IconList.Item icon={largeCardPoint1Icon} key="1">
              {largeCardPoint1}
            </IconList.Item>
          ),
          largeCardPoint2 && (
            <IconList.Item icon={largeCardPoint2Icon} key="2">
              {largeCardPoint2}
            </IconList.Item>
          ),
        ]}
        price={priceLabel}
        priceOriginal={savings && savings.was_price.formatted}
        renderImage={
          useCarousel && renderImage
        }
        renderAppendix={renderAppendix}
        title={title}
        href={
          !shouldForceFormOpen || !shouldShowCreatorForm
            ? url
            : undefined
        }
        onlyLinkInner={useCarousel}
        tags={showProductTags ? productTags : []}
      />
    );
  }
);

const ProductGrid = props => {
  const { userUiConfigState } = useContext(UserUiConfigContext);
  const [ items, setItems ] = useState(props.items);

  useEffect(() => {
    const sortedItems = sortCards(props.items, userUiConfigState.productGridSort);
    setItems(sortedItems);
  }, [ userUiConfigState ]);

  return (
    <section
      className={cx(
        'product-grid',
        { 'product-grid--no-margin-top': props.noMarginTop },
      )}>
      <SectionHeading tag="h4">
        {props.title}
      </SectionHeading>
      <div className="container-guttered">
        {!isEmpty(props.description) && (
          <div className="product-grid__description">
            <RichText fields={props.description} />
          </div>
        )}
        <Grid>
          {items.map((item, idx) => {
            let width = 12;
            let tabletWidth = 12;

            if (item.size === 'medium') {
              width = 6;
              tabletWidth = 6;
            } else if (item.size === 'small') {
              width = 4;
              tabletWidth = 6;
            } else if (item.size === 'extra-small') {
              width = 3;
              tabletWidth = 6;
            }

            if (item.size === 'large') {
              largeCardCount += 1;
            }

            return (
              <Grid.Item
                key={item.product.main.uid}
                width={width}
                tabletWidth={tabletWidth}
                extraPadding={item.extraBottomSpacing && item.size === 'large'}>
                {item.isMarketingCard
                  ? <ProductGridMarketingCard {...item.product} />
                  : getProductCard(item, props, largeCardCount)
                }
              </Grid.Item>
            );
          })}
        </Grid>
      </div>
    </section>
  );
};

ProductGrid.propTypes = {
  ageRangeLabel: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaCloseLabel: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.array,
  noMarginTop: PropTypes.bool,
  expandedId: PropTypes.string,
  setExpandedId: PropTypes.func,
  fetchPrismicProduct: PropTypes.func,
  media: PropTypes.shape({ fabletDown: PropTypes.bool }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      badgeOverride: PropTypes.string,
      ctaBehaviour: PropTypes.oneOf(['show-creator-form', 'open-product-page']),
      colourOverride: PropTypes.string,
      extraBottomSpacing: PropTypes.bool,
      forceOpenForm: PropTypes.bool,
      imageSize: PropTypes.oneOf(imageSizeOptions).isRequired,
      largeCardImageSide: PropTypes.oneOf(['auto', 'left', 'right']).isRequired,
      largeCardPoint1: PropTypes.string,
      largeCardPoint1Icon: PropTypes.string,
      largeCardPoint2: PropTypes.string,
      largeCardPoint2Icon: PropTypes.string,
      price: PropTypes.shape({
        amount: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired
      }).isRequired,
      product: PropTypes.any,
      savings: PropTypes.shape({
        was_price: PropTypes.shape({
          amount: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired
        }).isRequired
      }),
      size: PropTypes.oneOf(sizeOptions).isRequired,
      useCarousel: PropTypes.bool,
    })
  ).isRequired
};

export default ProductGrid;
